"use client";

import { ReactNode, useEffect, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useAuth } from "@/frontend/contexts/auth-context";
import { isPublicRoute, extractLocale, extractPathWithoutLocale } from "@/frontend/config/routes";
import { buildLoginUrlWithRedirect } from "@/frontend/lib/utils/redirect";
import { Spinner } from "@components/ui/spinner";

/**
 * AuthGuard - Protège TOUTES les routes par défaut
 *
 * Seules les routes publiques (définies dans config/routes.ts) sont accessibles sans authentification.
 * Toutes les autres routes nécessitent une authentification.
 *
 * Pour les routes admin, utiliser <ProtectedRoute requireAdmin> en plus.
 */

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Hook personnalisé pour gérer la logique de redirection
 * Séparé du rendu pour plus de clarté
 */
function useAuthRedirection() {
  const { user, loading } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    // Attendre que l'auth soit chargée
    if (loading || !pathname) return;

    // Vérifier si la route nécessite une authentification
    const needsAuth = !isPublicRoute(pathname);

    // Si pas d'utilisateur sur route protégée -> redirect login
    if (!user && needsAuth) {
      // Inclure les query params dans le path complet
      const fullPath = window.location.pathname + window.location.search;
      const pathWithoutLocale = extractPathWithoutLocale(fullPath);
      const locale = extractLocale(pathname);

      // Use centralized function to build login URL with optional redirect parameter
      const loginUrl = buildLoginUrlWithRedirect(locale, pathWithoutLocale);
      router.push(loginUrl);
    }

    // Note: Les utilisateurs authentifiés peuvent accéder aux routes publiques et protégées
  }, [user, loading, pathname, router]);
}

/**
 * Hook personnalisé pour déterminer l'état d'affichage
 * Séparé de la redirection pour plus de clarté
 */
function useAuthDisplayState() {
  const { user, loading } = useAuth();
  const pathname = usePathname();

  // Calculer l'état d'affichage (mémorisé pour éviter recalculs inutiles)
  const displayState = useMemo(() => {
    // Cas 1: Pas de pathname
    if (!pathname) {
      return { shouldShowLoader: false, shouldShowContent: false };
    }

    // Cas 2: Chargement en cours
    if (loading) {
      return { shouldShowLoader: true, shouldShowContent: false };
    }

    // Cas 3: Pas d'utilisateur sur route protégée (redirection en cours)
    const needsAuth = !isPublicRoute(pathname);
    if (!user && needsAuth) {
      return { shouldShowLoader: true, shouldShowContent: false };
    }

    // Cas 4: Tout est OK
    return { shouldShowLoader: false, shouldShowContent: true };
  }, [user, loading, pathname]);

  return displayState;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const t = useTranslations("loading");

  // Hook 1: Gère la redirection (side effect)
  useAuthRedirection();

  // Hook 2: Détermine quoi afficher (état)
  const { shouldShowLoader, shouldShowContent } = useAuthDisplayState();

  // Rendu conditionnel basé sur l'état
  if (shouldShowLoader) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-muted-foreground text-sm">{t("auth")}</p>
        </div>
      </div>
    );
  }

  if (shouldShowContent) {
    return <>{children}</>;
  }

  // Cas: pas de pathname (ne devrait jamais arriver)
  return null;
}
